import React from 'react'
import SwipeableViews from 'react-swipeable-views'
import {
  Avatar,
  RadioGroup,
  stringAvatar,
  Tooltip,
  Grid,
  Switch,
} from '@yuno/design-system-web'
import { PermissionsAvailable } from '@yuno/dashboard-authentication-mfe'
import { useAccountMenu, useLogout } from './hooks'
import {
  AccountListItem,
  AccountsContainer,
  AccountsCounter,
  ContentContainer,
  DividerStyled,
  IconStyled,
  MenuHeaderContainerStyled,
  MenuStyled,
  SeeAllAccounts,
  SeeAllAccountsContainer,
  StyleAccountContainer,
  StyledAccount,
  StyledContainer,
  StyledMenuHeaderLabel,
  AccountMenuProps,
  FIRST_TAB,
  SECOND_TAB,
  AvatarStyled,
  StyledRole,
  RadioStyled,
  StyledMenuItemLabel,
  CustomLabelStyled,
  CustomLabelContainerStyled,
} from './account-menu.styled'
import { AccountMenuItem, ManageAccountMenuItem } from '.'
import {
  useTranslation,
  useVerifyPermission,
} from '../../yuno-dashboard-container-mfe'
import AccountMenuTooltip from './account-menu-tooltip'
import { sections as rawSections } from '../../utils/constants'
import { AccountMenuItemName } from './account-menu-item-name'
import { useMediaQuery } from '@mui/material'

export const AccountMenu = ({
  accounts,
  handleSection,
  profile,
  hidden,
  handleSwitch,
  checkedSwitch,
}: React.PropsWithChildren<AccountMenuProps>) => {
  const { allowed } = useVerifyPermission(PermissionsAvailable.MANAGE_ACCOUNTS)
  const { allowed: allowedTeams } = useVerifyPermission('view_team_and_roles')
  const { t } = useTranslation()

  const sections = rawSections({ t })

  const {
    open,
    handleClick,
    handleClose,
    accountSplit,
    selectedTabIndex,
    selectedAccount,
    anchorEl,
    setSelectedTabIndex,
    onSelectAccount,
    handleNav,
  } = useAccountMenu({ accounts, handleSection })
  const { handleLogout } = useLogout()
  const isMobile = useMediaQuery('(max-width: 599px)')

  return (
    <>
      <Tooltip
        color={'black width-auto'}
        placement={'bottom-end'}
        title={
          <AccountMenuTooltip account={selectedAccount} profile={profile} />
        }
        arrow
      >
        <StyledContainer
          onClick={handleClick}
          aria-controls={open ? 'account-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
        >
          <>
            <AvatarStyled>{accountSplit}</AvatarStyled>
            <IconStyled name="CaretDown" weight="light" />
          </>
        </StyledContainer>
      </Tooltip>

      <MenuStyled
        className="menu-account"
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <SwipeableViews index={selectedTabIndex} animateHeight>
          <section>
            <ContentContainer>
              <StyleAccountContainer>
                <StyledAccount>
                  <AvatarStyled noHover>{accountSplit}</AvatarStyled>
                  <div>
                    <AccountMenuItemName
                      selectedAccount={selectedAccount}
                      profile={profile}
                      name={selectedAccount?.name}
                    />
                    <StyledRole>{profile.rol_name}</StyledRole>
                  </div>
                </StyledAccount>
                <SeeAllAccountsContainer
                  onClick={setSelectedTabIndex.bind(null, SECOND_TAB)}
                >
                  <SeeAllAccounts>{t('sections.seeAall')}</SeeAllAccounts>
                  <AccountsCounter>{accounts.length}</AccountsCounter>
                </SeeAllAccountsContainer>
              </StyleAccountContainer>
              <AccountMenuItem
                iconName="User"
                label={t('sections.profile')}
                onClick={handleNav(sections.profile.path)}
              />
              <AccountMenuItem
                iconName="Lock"
                label={t('sections.security')}
                onClick={handleNav(sections.security.path)}
              />
              <AccountMenuItem
                iconName="Gear"
                label={t('sections.settings')}
                onClick={handleNav(sections.settings.path)}
              />
              {allowedTeams && (
                <AccountMenuItem
                  iconName="UserPlus"
                  label={t('sections.team')}
                  onClick={handleNav(sections.team.path)}
                />
              )}
              <AccountMenuItem
                iconName="Question"
                label={t('sections.help')}
                onClick={handleNav(sections.help.path)}
              />
              {isMobile && (
                <CustomLabelContainerStyled>
                  <AccountMenuItem
                    iconName="ToggleRight"
                    label={
                      <CustomLabelStyled>
                        <StyledMenuItemLabel>{`${t('testMode')} · ${
                          checkedSwitch ? t('on') : t('off')
                        }`}</StyledMenuItemLabel>
                        <Switch
                          checked={checkedSwitch}
                          onClick={handleSwitch}
                          label={``}
                          labelPlacement="start"
                        />
                      </CustomLabelStyled>
                    }
                  />
                </CustomLabelContainerStyled>
              )}
              <AccountMenuItem
                iconName="SignOut"
                label={t('sections.logout')}
                onClick={handleLogout}
              />
            </ContentContainer>
          </section>
          <section>
            <MenuHeaderContainerStyled>
              <IconStyled
                name="ArrowLeft"
                onClick={setSelectedTabIndex.bind(null, FIRST_TAB)}
              />
              <StyledMenuHeaderLabel>
                {t('account_menu.select_account')}
              </StyledMenuHeaderLabel>
              <IconStyled name="X" onClick={handleClose} />
            </MenuHeaderContainerStyled>
            <AccountsContainer>
              <RadioGroup value={selectedAccount?.name}>
                {accounts
                  ?.sort((a, b) => {
                    const aDate = new Date(b.created_at)
                    const bDate = new Date(a.created_at)

                    return Number(bDate) - Number(aDate)
                  })
                  .map((account) => (
                    <AccountListItem
                      container
                      key={account.code}
                      onClick={onSelectAccount.bind(null, account)}
                    >
                      <Grid item xs={2}>
                        <Avatar>{stringAvatar(account.name)}</Avatar>
                      </Grid>
                      <Grid item xs={9}>
                        <AccountMenuItemName
                          selectedAccount={account}
                          profile={profile}
                          name={account.name}
                        />
                      </Grid>
                      <Grid item xs={1}>
                        <RadioStyled value={account.name} />
                      </Grid>
                    </AccountListItem>
                  ))}
              </RadioGroup>
              {allowed && !hidden && (
                <Grid item>
                  <DividerStyled />
                  <ManageAccountMenuItem
                    label={t('account_menu.manage_accounts')}
                    onClick={handleNav('/accounts')}
                  />
                </Grid>
              )}
            </AccountsContainer>
          </section>
        </SwipeableViews>
      </MenuStyled>
    </>
  )
}
